/**
 * Product sidebar configuration.
 *
 * The sidebar shows:
 *   1. the active main product (label from `resolvePackageLabel`) with the
 *      working pages nested below it,
 *   2. the packages in the lower slot: the booked ones (`subscribedFor`, shown
 *      as active) and the offers — Kjuup offers Kjuup+ and Kjuup.io, Kjuup+
 *      offers Kjuup.io. Coming-soon products stay hidden.
 *
 * kind:
 *   "internal"    -> in-app route
 *   "external"    -> opens `url` in a new tab
 *   "coming_soon" -> teaser modal (until PRODUCT_URLS.<id> is set)
 *
 * `visibleFor(persona)` receives the object from `resolveCockpitPersona`.
 */
import appConfig from "./appConfig";
import kjuupIoLogo from "../Icons/KJUUPio-Icon.svg";
import kjuupPlusLogo from "../Icons/KjuupPlus-Icon.svg";

const { PRODUCT_URLS } = appConfig;

export const ADDON_PRODUCTS = [
  {
    id: "kjuup_plus",
    label: "Kjuup+",
    subtitleKey: "products_kjuup_plus_sub",
    icon: "domain",
    iconSrc: kjuupPlusLogo,
    // `tile` wins in the sidebar: the package tile of the Plans page (PackageTile).
    tile: "plus",
    // The offer (prices, features, request) lives on the cockpit.
    kind: "internal",
    route: "/?section=kjuup-plus",
    // Member of a Kjuup+ company: the entry opens the booking on the cockpit's company view.
    subscribedFor: (p) => !!p.isKjuupPlusMember,
    subscribedRoute: "/?tab=company&section=kjuup-plus-abo",
    subscribedSubtitleKeyFor: () => "products_kjuup_plus_sub_active",
    visibleFor: (p) => !p.isKjuupPlusMember,
  },
  {
    id: "kjuup_io",
    label: "Kjuup.io",
    subtitleKey: "products_kjuup_io_sub",
    // Enabled and the free trial still open: "kostenlos testen" instead of "testen oder buchen".
    subtitleKeyFor: (p) => (p.canStartTrial ? "products_kjuup_io_sub_trial" : "products_kjuup_io_sub"),
    icon: "auto_awesome",
    // `iconSrc` wins over `icon`: the entry carries the Kjuup.io brand mark.
    iconSrc: kjuupIoLogo,
    tile: "io",
    // The offer lives on the cockpit: bookable once Kjuup.io is enabled for the
    // user (Admin Panel), otherwise shown locked with a request link.
    kind: "internal",
    route: "/?section=kjuup-io",
    // Not enabled for the user in the Admin Panel: the sidebar shows the entry greyed out with
    // "Coming soon" and no link (the cockpit offer carries the same badge).
    comingSoonFor: (p) => !p.stripeUiEnabled,
    // Booked: the entry manages the subscription (see openKjuupIoSubscription).
    subscribedFor: (p) => !!p.hasActiveKjuupIoPlan,
    subscribedSubtitleKeyFor: (p) => (p.trialing ? "products_sub_trialing" : "products_sub_active"),
    visibleFor: (p) => !p.hasActiveKjuupIoPlan,
  },
];

export const OTHER_PRODUCTS = [
  {
    id: "josch",
    label: "Josch.IO",
    subtitleKey: "products_josch_sub",
    icon: "graphic_eq",
    kind: PRODUCT_URLS.josch ? "external" : "coming_soon",
    url: PRODUCT_URLS.josch || null,
  },
  {
    id: "projektview",
    label: "ProjektView",
    subtitleKey: "products_projektview_sub",
    icon: "bar_chart",
    kind: PRODUCT_URLS.projektview ? "external" : "coming_soon",
    url: PRODUCT_URLS.projektview || null,
  },
  {
    id: "kostenprognose",
    label: "Kostenprognose.IO",
    subtitleKey: "products_kostenprognose_sub",
    icon: "trending_up",
    kind: PRODUCT_URLS.kostenprognose ? "external" : "coming_soon",
    url: PRODUCT_URLS.kostenprognose || null,
  },
  {
    id: "terminplan",
    label: "Terminplan.IO",
    subtitleKey: "products_terminplan_sub",
    icon: "calendar_month",
    kind: PRODUCT_URLS.terminplan ? "external" : "coming_soon",
    url: PRODUCT_URLS.terminplan || null,
  },
];

/** Resolve the concrete target of an add-on entry for a persona. */
export function resolveProductTarget(entry, persona) {
  if (!entry) return { kind: "coming_soon" };
  if (typeof entry.resolve === "function") {
    const resolved = entry.resolve(persona || {});
    if (resolved) return { ...entry, ...resolved };
  }
  return entry;
}

export function visibleAddons(persona) {
  return ADDON_PRODUCTS.filter((e) => {
    try {
      return typeof e.visibleFor === "function" ? !!e.visibleFor(persona || {}) : true;
    } catch (_) {
      return false;
    }
  });
}

/** Whether the persona has booked this package entry. */
export function isProductSubscribed(entry, persona) {
  return !!entry && typeof entry.subscribedFor === "function" && !!entry.subscribedFor(persona || {});
}

/**
 * Lower sidebar slot: offers only. Coming-soon products are hidden on every
 * package, booked ones are listed in the personal settings instead. Kjuup
 * offers Kjuup+ and Kjuup.io. Kjuup+ offers Kjuup.io.
 */
export function sidebarPackageOptions(persona) {
  const id = persona && persona.persona;
  return ADDON_PRODUCTS.filter((entry) => {
    if (isProductSubscribed(entry, persona)) return false;
    if (entry.id === "kjuup_plus") return id === "kjuup";
    if (entry.id === "kjuup_io") return id === "kjuup" || id === "plus";
    return false;
  });
}
